import axios from "axios";
import * as React from "react";
import { singletonHook } from "react-singleton-hook";
import { getOidcConfig } from "./oidcConfig";

const ONE_HOUR = 60 * 60 * 1000; // 1 hour in milliseconds

export interface AttendeeMetadata {
    terms: boolean;
    privacy: boolean;
    marketing: boolean;
}

export interface Attendee {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
    school: string;
    hasSeenWelcomePage: boolean;
    favorites: string[];
    visited: string[];
    mySchedule: string[];
    metadata: string;
}

export const isProfileComplete = (profile: Attendee) => {
    if (!profile) {
        return false;
    }

    return profile.firstName?.trim().length > 0 && profile.lastName?.trim().length > 0 && profile.email?.trim().length > 0 && profile.mobileNumber?.trim().length > 0;
};

export const GetAttendeeMetadata = (attendee: Attendee): AttendeeMetadata => {
    return JSON.parse(attendee.metadata || "{}") as AttendeeMetadata;
};

export const CreateAttendee = async (): Promise<Attendee> => {
    let attendeeJSON = await localStorage.getItem("attendee");
    let lastFetchTime = await localStorage.getItem("attendeeLastFetch");

    // console.log(attendeeJSON);
    if (attendeeJSON) {
        try {
            let attendee = JSON.parse(attendeeJSON) as Attendee;

            if (attendee && attendee.id) {
                // Check if we've fetched within the last hour
                const now = Date.now();
                const lastFetch = lastFetchTime ? parseInt(lastFetchTime) : 0;
                const timeSinceLastFetch = now - lastFetch;

                // If fetched within the last hour, return cached data
                if (timeSinceLastFetch < ONE_HOUR) {
                    return attendee;
                }

                // Otherwise, fetch fresh data from server
                let attendeeRes = await axios.get(`${Server}/api/attendees/${attendee.id}`);
                // console.log(attendeeRes);
                await localStorage.setItem("attendee", JSON.stringify(attendeeRes.data));
                await localStorage.setItem("attendeeLastFetch", now.toString());
                attendeeRes.data.metadata = attendee.metadata;
                return attendeeRes.data;
            }
        } catch (e) {
            // console.log(e);
        }
    }

    let res = await axios.post(`${Server}/api/attendees`, {
        firstName: "",
        lastName: "",
        email: "",
        mobileNumber: "",
        school: "",
        hasSeenWelcomePage: false,
        favorites: [],
        visited: [],
        mySchedule: [],
    });

    // console.log(res);

    if (res.status === 200 && res.data) {
        await localStorage.setItem("attendee", JSON.stringify(res.data));
        await localStorage.setItem("attendeeLastFetch", Date.now().toString());
        return res.data as Attendee;
    }
    return {} as Attendee;
};

export const UpdateProfile = async (attendee: Attendee) => {
    await localStorage.setItem("attendee", JSON.stringify(attendee));
    await localStorage.setItem("attendeeLastFetch", (Date.now() - ONE_HOUR * 2).toString());

    return axios.post(`${Server}/api/attendees/updateProfile`, {
        userID: attendee.id,
        firstName: attendee.firstName,
        lastName: attendee.lastName,
        email: attendee.email,
        mobileNumber: attendee.mobileNumber,
        school: attendee.school,
        hasSeenWelcomePage: true,
        favorites: attendee.favorites,
        visited: attendee.visited,
        mySchedule: attendee.mySchedule,
    });
};

export const UpdateMetadata = async (attendee: Attendee, metadata: AttendeeMetadata) => {
    return axios.post(`${Server}/api/attendees/updateMetadata`, {
        userID: attendee.id,
        metadata: JSON.stringify(metadata),
    });
};

const init = { user: {} as Attendee, getUser: () => {} };

const userFactory = () => {
    const [user, setUser] = React.useState({} as Attendee);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        if (!user.id && !isLoading) {
            setIsLoading(true);
            CreateAttendee()
                .then((newUser) => {
                    setUser(newUser);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Error creating attendee:", error);
                    setIsLoading(false);
                });
        }
    }, [user.id, isLoading]);

    const getUser = React.useCallback(async () => {
        if (isLoading) return; // Prevent concurrent calls

        setIsLoading(true);
        try {
            const newUser = await CreateAttendee();
            setUser(newUser);
        } catch (error) {
            console.error("Error getting user:", error);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading]);

    return { user, getUser };
};

export const useUser = singletonHook(init, userFactory);
